import { XMarkIcon } from "@heroicons/react/20/solid";
import {
    DocumentArrowDownIcon,
    EnvelopeIcon,
    LinkIcon,
    PlusIcon,
    PrinterIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import * as Common from "@sp-crm/core";
import {
    ActionType,
    ContactId,
    isNonNilEntityId,
    Localization,
    PlacementId,
} from "@sp-crm/core";
import { AiSummaryState } from "components/ai-summary/ai-summary-state";
import { useClientAiSummary } from "components/ai-summary/use-client-ai-summary";
import { InputFormBlock } from "components/app/input-form-block";
import { NgByline } from "components/byline/ng-byline";
import { AiSummaryBanner } from "components/details-page-v-next/ai-summary-banner";
import { GenerateAiSummaryButton } from "components/details-page-v-next/generate-ai-summary-button";
import { HeaderNameActions } from "components/details-page-v-next/header-name-actions";
import { ShowEditDetailsButton } from "components/details-page-v-next/show-edit-details-button";
import { Activity } from "components/entity-activity/entity-activity";
import { SignatureRequestPanel } from "components/files/signature-request/signature-request-panel";
import { EntityPageHeader } from "components/layout";
import { EntityConversationFeed } from "components/messages/entity-conversation-feed";
import { MessageCompose } from "components/messages/message-compose";
import { MessagesTabHeader } from "components/messages/messages-tab-header";
import { SpecialComposeCommmunityComparisonProps } from "components/messages/special/community-comparison";
import { ImportantNoteBanner } from "components/notes/important-note-banner";
import { DetailPageLayout } from "components/shared/detail-page-layout";
import { DuplicateRecordBanner } from "components/shared/duplicate-record-banner";
import {
    QueryInvalidator,
    QueryInvalidatorHandle,
} from "components/store-aware-query-client-provider/invalidator";
import {
    ActionsMenu,
    ActionsMenuAction,
    ActionsMenuHeader,
    ActionsMenuSection,
    CollaboratorsMenuAction,
    FaxMenuAction,
    NotifyUnselectedCommunitiesMenuAction,
    PaperIntakeMenuAction,
    ShareInputFormMenuAction,
    SignWiseMenuAction,
    TransferClientMenuAction,
} from "components/ui/actions-menu";
import { AutosavingInput } from "components/ui/autosaving-input";
import { AutosavingStateInput } from "components/ui/autosaving-state-input";
import { CheckboxUncontrolled } from "components/ui/checkbox";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import * as React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { useFeature, useIsAllowedInWholeTenant } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { ApplicationState } from "store/state";
import { twoLineFormattedAddress } from "util/address";
import { handleEvent } from "util/user-events";
import {
    AdvancedSearchEntityType,
    CommunityOrdering,
    FileEntityType,
} from "../../../generated/graphql";
import { currentLocale } from "../../../store/selectors/regions";
import { isAllowed } from "../../../util/permissions";
import { FilesControl } from "../../files";
import { Header } from "../../header";
import { LocaleContext } from "../../locale-provider/locale-provider";
import { RegionSelect } from "../../region-select";
import Shared from "../../shared";
import { Col, NgGrid, Row } from "../../shared/grid";
import { SubnavItem } from "../../shared/subnav/subnav-types";
import { EntityTasks, TaskBadge } from "../../tasks/child";
import { ClientNamesHeader } from "../client-names-header";
import { EngagementTabs, useDeleteNavigation } from "../engagements/engagement-tabs";
import { StartHomeCareMenuAction } from "../engagements/start-engagement-menu-actions";
import { InvoiceInput } from "../invoices";
import { ClientTransferBanner } from "../transfers/client-transfer-banner";
import { TransferClientPanel } from "../transfers/transfer-client-panel";
import { placementTransferLocation } from "../transfers/transfer-types";
import { ShowClientActionBar } from "./action-bar";
import { CareProviders } from "./care-providers";
import { hasClientDetails } from "./client-details";
import { ClientDetailsBlock } from "./client-details-block";
import { CollaboratorsHeaderLink } from "./collaborators-header-link";
import { ClientCommunity } from "./community";
import { CommunityComparisonOrderable } from "./community-comparison/community-comparison-orderable";
import { CommunitiesComparePdfDialog } from "./community-comparison/community-pdf-dialog";
import { EmailCommunityComparisonCustomization } from "./community-comparison/email-community-comparison-customization";
import { LiveCommunityComparisonLink } from "./community-comparison/live-comparison-link";
import { InsuranceDetails } from "./insurance-details";
import { ClientIntakeUploadDialog } from "./intake/client-intake-upload-dialog";
import { ClientIntakeDownloadDialog } from "./intake/download-dialog";
import { ClientIntakeFax } from "./intake/fax";
import {
    ClientIntakeEmailDialogToClient,
    ClientIntakeEmailDialogToCommunity,
    NotifyUnselectedCommunitiesDialog,
} from "./intake/intake";
import { InvoiceTab } from "./invoices/invoice-tab";
import { ManageCollaboratorsModal } from "./manage-collaborators-modal";
import { NoActiveTasksPrompt } from "./no-active-tasks-prompt";
import { PlacementOverviewTab } from "./placement-overview-tab";
import ClientQuestionSection from "./question-section";
import { ShowClientReferrals } from "./referrals";
import { ShareInputFormLinkPanel } from "./share-input-form-link-panel";

const TIME_TRACKING_BANNER_KEY = "dismissed:timeTrackingBanner";

const ActivityTabContent: React.FC<{ clientId: Common.PlacementId }> = ({ clientId }) => {
    const trackTimeFeature = useFeature("trackTime");
    const { settings } = useTenantSettings();
    const showAgencySettings = useIsAllowedInWholeTenant(ActionType.UpdateTenant);
    const [dismissed, setDismissed] = React.useState(
        () => localStorage.getItem(TIME_TRACKING_BANNER_KEY) === "true",
    );

    const showBanner =
        trackTimeFeature &&
        !settings.trackTimeEnabled &&
        showAgencySettings &&
        !dismissed;

    return (
        <div className="space-y-4">
            {showBanner ? (
                <InlineBanner type="info">
                    <div className="flex items-center justify-between">
                        <span>
                            Enable time tracking in{" "}
                            <Link
                                to="/settings/agency/clients"
                                className="underline font-medium">
                                agency settings
                            </Link>{" "}
                            to track billable hours here.
                        </span>
                        <SecondaryCompactButton
                            className="ml-4 shrink-0"
                            onClick={() => {
                                localStorage.setItem(TIME_TRACKING_BANNER_KEY, "true");
                                setDismissed(true);
                            }}>
                            <XMarkIcon className="h-5 w-5" />
                        </SecondaryCompactButton>
                    </div>
                </InlineBanner>
            ) : null}
            <Activity entityId={clientId} entityType="Client" />
        </div>
    );
};

export type Subpage =
    | "overview"
    | "needs"
    | "communities"
    | "tasks"
    | "compareCommunities"
    | "invoices"
    | "referrals"
    | "emailsSent"
    | "files"
    | "history";

enum OverlayExperience {
    None,
    ClientIntakeDownload,
    ClientIntakeUpload,
    ClientIntakeEmailToCommunities,
    ClientIntakeFaxToCommunities,
    CommunityComparisonDownload,
    CommunityComparisonEmail,
    GenericClientEmail,
    ShareLink,
    Signature,
    ClientDisclosureEmail,
    TransferClient,
    ShareInputFormLink,
    ManageCollaborators,
    NotifyUnselectedCommunities,
}

interface ShowClientOwnProps {
    placement: Common.IPlacement;
    triggerClientLoad: (id: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onFieldChange: (fieldName: string, newValue: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onFieldChangeMulti: (newValues: any) => void;
    onClientCommunityOrderChange: (
        placementId: PlacementId,
        ordering: CommunityOrdering[],
    ) => void;
    newlyAddedContactPerson?: ContactId;
    onUpdateAnswer: (answer: Common.IAnswer) => void;
    delete: (navigateTo: string) => void;
    reactivate: () => void;
    getShareLink: () => Promise<string>;
    questions: Common.IQuestion[];
    users: Common.User[];
    user: Common.User;
    tenant: Common.ITenant;
    now: Date;
    activeSubpage: Subpage;
    selectSection: (section: Subpage) => void;
    closeSection: () => void;
    pdfRenderable: boolean;
    fileCount: number;
    navigateToClients: () => void;
    navigateToPrintIntakeForm: () => void;
}

interface ShowClientPropsFromState {
    showInvoicesTab: boolean;
    showReferralsTab: boolean;
    locale: Localization;
    shareInputFormLinkEnabled: boolean;
    responsiveMode: number;
}

interface ShowClientPropsFromFeatures {
    placementDetailVNext: boolean;
    resolveDeleteNavigation: () => string;
    aiSummary: AiSummaryState | null;
}

type ShowClientProps = ShowClientOwnProps &
    ShowClientPropsFromFeatures &
    ShowClientPropsFromState;

interface ShowClientState {
    detailsEditing: boolean;
    isCommunitiesPdfRequested: boolean;
    isNoteChanged: boolean;
    newlyAddedContactPerson?: ContactId;
    overlayExperience: OverlayExperience;
    previouslyAddedContactPerson?: ContactId;
    taskAlertsEnabled: boolean;
}

class ShowClient extends React.Component<ShowClientProps, ShowClientState> {
    private invalidatorRef = React.createRef<QueryInvalidatorHandle>();

    constructor(p: ShowClientProps) {
        super(p);
        this.state = {
            detailsEditing: !hasClientDetails({
                client: p.placement,
                bestContact: p.placement.bestContactEntity,
            }),
            isCommunitiesPdfRequested: false,
            isNoteChanged: false,
            newlyAddedContactPerson: null,
            previouslyAddedContactPerson: null,
            overlayExperience: OverlayExperience.None,
            taskAlertsEnabled: true,
        };
        this.toggleDetailsEditing = this.toggleDetailsEditing.bind(this);
        this.activityTab = this.activityTab.bind(this);
        this.closeOverlay = this.closeOverlay.bind(this);
        this.communityWorkflowFindIdealTab =
            this.communityWorkflowFindIdealTab.bind(this);
        this.communityWorkflowOptionsTab = this.communityWorkflowOptionsTab.bind(this);
        this.emailsSentTab = this.emailsSentTab.bind(this);
        this.filesTab = this.filesTab.bind(this);
        this.invoicesTab = this.invoicesTab.bind(this);
        this.needsTab = this.needsTab.bind(this);
        this.overlayClientIntakeDownload = this.overlayClientIntakeDownload.bind(this);
        this.overlayClientIntakeUpload = this.overlayClientIntakeUpload.bind(this);
        this.overlayClientIntakeEmailToCommunities =
            this.overlayClientIntakeEmailToCommunities.bind(this);

        this.overlayClientIntakeFaxToCommunities =
            this.overlayClientIntakeFaxToCommunities.bind(this);
        this.overlayCommunityComparisonDownload =
            this.overlayCommunityComparisonDownload.bind(this);
        this.overlayCommunityComparisonEmail =
            this.overlayCommunityComparisonEmail.bind(this);
        this.overlaySendGenericClientEmail =
            this.overlaySendGenericClientEmail.bind(this);
        this.overlayShareLink = this.overlayShareLink.bind(this);
        this.overlaySignature = this.overlaySignature.bind(this);
        this.overviewTab = this.overviewTab.bind(this);
        this.referralsTab = this.referralsTab.bind(this);
        this.startDelete = this.startDelete.bind(this);
        this.startReactivate = this.startReactivate.bind(this);
        this.overlayClientDisclosureEmail = this.overlayClientDisclosureEmail.bind(this);
        this.overlayTransferClient = this.overlayTransferClient.bind(this);
        this.overlayShareInputFormLink = this.overlayShareInputFormLink.bind(this);
        this.overlayManageCollaborators = this.overlayManageCollaborators.bind(this);
        this.overlayNotifyUnselectedCommunities =
            this.overlayNotifyUnselectedCommunities.bind(this);
        this.tasksTab = this.tasksTab.bind(this);
    }

    UNSAFE_componentWillReceiveProps(props: ShowClientProps) {
        if (props.newlyAddedContactPerson != this.state.previouslyAddedContactPerson) {
            // only update state if we've actually received a *new* contact person
            this.setState({
                ...this.state,
                newlyAddedContactPerson: props.newlyAddedContactPerson,
            });
        }
    }

    private toggleDetailsEditing(): void {
        this.setState(current => ({ detailsEditing: !current.detailsEditing }));
    }

    private closeOverlay(): void {
        this.refreshClientCommunities();
        this.setState({ overlayExperience: OverlayExperience.None });
    }

    private async refreshClientCommunities() {
        if (this.invalidatorRef.current) {
            await this.invalidatorRef.current.invalidateQueries("getClientCommunities");
        }
    }

    private async startDelete() {
        const shouldDelete = await fancyConfirm(
            `Delete ${this.props.placement.preferredClientName}?`,
            `Really delete ${this.props.placement.preferredClientName}?`,
            "Yes, delete",
            "Cancel",
            { extraConfirm: true },
        );
        if (shouldDelete) {
            this.setState({ taskAlertsEnabled: false }, () => {
                setTimeout(() => {
                    this.props.delete(this.props.resolveDeleteNavigation());
                }, 0);
            });
        }
    }

    private overlayClientIntakeDownload(): void {
        this.setState({ overlayExperience: OverlayExperience.ClientIntakeDownload });
    }

    private overlayClientIntakeUpload(): void {
        this.setState({ overlayExperience: OverlayExperience.ClientIntakeUpload });
    }

    private overlayClientIntakeEmailToCommunities() {
        this.setState({
            overlayExperience: OverlayExperience.ClientIntakeEmailToCommunities,
        });
    }

    private overlayClientIntakeFaxToCommunities() {
        this.setState({
            overlayExperience: OverlayExperience.ClientIntakeFaxToCommunities,
        });
    }

    private overlayCommunityComparisonDownload() {
        this.setState({
            overlayExperience: OverlayExperience.CommunityComparisonDownload,
        });
    }

    private overlayCommunityComparisonEmail() {
        this.setState({ overlayExperience: OverlayExperience.CommunityComparisonEmail });
    }

    private overlaySendGenericClientEmail() {
        this.setState({ overlayExperience: OverlayExperience.GenericClientEmail });
    }

    private overlaySignature(): void {
        this.setState({ overlayExperience: OverlayExperience.Signature });
    }

    private overlayShareLink(): void {
        this.setState({ overlayExperience: OverlayExperience.ShareLink });
    }

    private overlayClientDisclosureEmail() {
        this.setState({ overlayExperience: OverlayExperience.ClientDisclosureEmail });
    }

    private overlayTransferClient() {
        handleEvent("client-action", {
            type: "transferClient",
        });
        this.setState({ overlayExperience: OverlayExperience.TransferClient });
    }

    private overlayShareInputFormLink() {
        this.setState({ overlayExperience: OverlayExperience.ShareInputFormLink });
    }

    private overlayManageCollaborators() {
        this.setState({ overlayExperience: OverlayExperience.ManageCollaborators });
    }

    private overlayNotifyUnselectedCommunities() {
        if (!isNonNilEntityId(this.props.placement.chosenCommunityId)) {
            fancyAlert(
                "Notify Communities",
                "Once you've set a chosen community for this client, you can send a follow-up email to the other communities from here.",
                "info",
            );
            return;
        }
        this.setState({
            overlayExperience: OverlayExperience.NotifyUnselectedCommunities,
        });
    }

    private async startReactivate() {
        handleEvent("client-action", {
            type: "startReactivate",
        });
        const shouldReactivate = await fancyConfirm(
            `Start a new move?`,
            `Start a new move for ${this.props.placement.name}? This will archive the current workflow and start a new one.`,
            "Yes, start a new move process",
            "Cancel",
            { extraConfirm: true },
        );
        if (shouldReactivate) {
            this.props.reactivate();
        }
    }

    private archivedMoveBanner(): JSX.Element {
        if (this.props.placement.archived) {
            return (
                <div className="px-4 pt-2">
                    <InlineBanner type="warning">
                        <p className="sp-overrides--large-message">
                            You are viewing a previous move for{" "}
                            {this.props.placement?.name || "(no name)"}.
                        </p>
                    </InlineBanner>
                </div>
            );
        }
        return null;
    }

    private transferClientBanner(): JSX.Element {
        return (
            <ClientTransferBanner
                subject={{ kind: "placement", id: this.props.placement.id }}
            />
        );
    }

    private duplicateWarning(): JSX.Element {
        return (
            <DuplicateRecordBanner
                recordType="placement"
                recordId={this.props.placement.id}
                nameKey={this.props.placement.name}
            />
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    private headerBar(): any {
        const communityComparisonDisabled = !this.props.placement.allCommunities.length;
        const namesHeader = (
            <ClientNamesHeader
                primaryClientName={this.props.placement.name}
                additionalClientName={this.props.placement.additionalClient?.name}
                bestContactName={this.props.placement.bestContactEntity?.name}
                serial={this.props.placement.serial}
            />
        );
        return (
            <EntityPageHeader>
                {this.props.placementDetailVNext ? (
                    <HeaderNameActions
                        name={namesHeader}
                        actions={
                            <>
                                <ShowEditDetailsButton
                                    mode="edit"
                                    editing={this.state.detailsEditing}
                                    onToggle={this.toggleDetailsEditing}
                                />
                                {this.props.aiSummary ? (
                                    <GenerateAiSummaryButton
                                        aiSummary={this.props.aiSummary}
                                        entityName={this.props.placement.name}
                                    />
                                ) : null}
                            </>
                        }
                    />
                ) : (
                    namesHeader
                )}
                <div className="flex items-center space-x-2 justify-end ml-auto">
                    <CollaboratorsHeaderLink
                        clientId={this.props.placement.id}
                        onClick={this.overlayManageCollaborators}
                    />
                    <div>
                        <NgByline
                            entity={this.props.placement}
                            mode="header"
                            createdAtDateModified={(newDate: Date) =>
                                this.props.onFieldChange(
                                    "dateAddedManualOverride",
                                    newDate,
                                )
                            }
                        />
                    </div>
                    <div>
                        <ActionsMenu>
                            <ActionsMenuSection>
                                <ActionsMenuHeader>
                                    Client Referral Details
                                </ActionsMenuHeader>
                                <ActionsMenuAction
                                    Icon={EnvelopeIcon}
                                    menuHidden={false}
                                    disabled={communityComparisonDisabled}
                                    onClick={this.overlayClientIntakeEmailToCommunities}>
                                    Email to community
                                </ActionsMenuAction>
                                <ActionsMenuAction
                                    Icon={PrinterIcon}
                                    menuHidden={false}
                                    onClick={() =>
                                        this.props.navigateToPrintIntakeForm()
                                    }>
                                    Printable Intake Form
                                </ActionsMenuAction>
                                <FaxMenuAction
                                    disabled={communityComparisonDisabled}
                                    onClick={this.overlayClientIntakeFaxToCommunities}
                                />
                                <ShareInputFormMenuAction
                                    shareInputFormLinkEnabled={
                                        this.props.shareInputFormLinkEnabled
                                    }
                                    onClick={this.overlayShareInputFormLink}
                                />
                                <ActionsMenuAction
                                    Icon={DocumentArrowDownIcon}
                                    menuHidden={false}
                                    disabled={!this.props.pdfRenderable}
                                    onClick={this.overlayClientIntakeDownload}>
                                    Download PDF
                                </ActionsMenuAction>
                                <PaperIntakeMenuAction
                                    disabled={!this.props.pdfRenderable}
                                    onClick={this.overlayClientIntakeUpload}
                                />
                            </ActionsMenuSection>
                            <ActionsMenuSection>
                                <ActionsMenuHeader>
                                    Community Comparison
                                </ActionsMenuHeader>
                                <ActionsMenuAction
                                    Icon={EnvelopeIcon}
                                    menuHidden={false}
                                    disabled={communityComparisonDisabled}
                                    onClick={this.overlayCommunityComparisonEmail}>
                                    Email to client
                                </ActionsMenuAction>
                                <ActionsMenuAction
                                    Icon={LinkIcon}
                                    menuHidden={false}
                                    disabled={communityComparisonDisabled}
                                    onClick={this.overlayShareLink}>
                                    Shareable link
                                </ActionsMenuAction>
                                <ActionsMenuAction
                                    Icon={DocumentArrowDownIcon}
                                    menuHidden={false}
                                    disabled={communityComparisonDisabled}
                                    onClick={this.overlayCommunityComparisonDownload}>
                                    Download PDF
                                </ActionsMenuAction>
                            </ActionsMenuSection>
                            <ActionsMenuSection>
                                <ActionsMenuHeader>Other Actions</ActionsMenuHeader>
                                <ActionsMenuAction
                                    Icon={EnvelopeIcon}
                                    menuHidden={false}
                                    onClick={this.overlaySendGenericClientEmail}>
                                    Send email
                                </ActionsMenuAction>
                                <SignWiseMenuAction onClick={this.overlaySignature} />
                                <NotifyUnselectedCommunitiesMenuAction
                                    onClick={this.overlayNotifyUnselectedCommunities}
                                />
                                <ActionsMenuAction
                                    Icon={PlusIcon}
                                    menuHidden={false}
                                    onClick={this.startReactivate}>
                                    Start new placement
                                </ActionsMenuAction>
                                <StartHomeCareMenuAction
                                    placementId={this.props.placement.id}
                                    clientName={this.props.placement.name}
                                />
                                <TransferClientMenuAction
                                    onClick={this.overlayTransferClient}
                                />
                                <CollaboratorsMenuAction
                                    onClick={this.overlayManageCollaborators}
                                />
                                <ActionsMenuAction
                                    Icon={TrashIcon}
                                    menuHidden={false}
                                    onClick={this.startDelete}>
                                    Delete
                                </ActionsMenuAction>
                            </ActionsMenuSection>
                        </ActionsMenu>
                    </div>
                </div>
            </EntityPageHeader>
        );
    }

    private tasksTab() {
        const assignedUserId = this.props.placement.assignedUserId || null;

        return (
            <Row type="flex" className="collapsible">
                <Col className="full-width">
                    <EntityTasks
                        parentId={this.props.placement.id}
                        defaultAssigneeId={assignedUserId}
                        entityInfo={{
                            displayName: this.props.placement.name,
                            path: `/clients/show/${this.props.placement.id}`,
                            kind: "Client",
                        }}
                    />
                </Col>
            </Row>
        );
    }

    private referralsTab() {
        return (
            <Row type="flex" className="collapsible">
                <Col className="full-width">
                    <ShowClientReferrals clientId={this.props.placement.id} />
                </Col>
            </Row>
        );
    }

    private emailsSentTab() {
        return (
            <EntityConversationFeed
                entityId={this.props.placement.id}
                entityType={AdvancedSearchEntityType.Client}
            />
        );
    }

    private activityTab() {
        return <ActivityTabContent clientId={this.props.placement.id} />;
    }

    private getInvoiceInputs(): InvoiceInput {
        const communityName =
            this.props.placement.clientCommunities?.find(
                cc => cc.community.id === this.props.placement.chosenCommunityId,
            )?.community.name || "";
        const moveDate = this.props.placement.moveTimelineActual;

        return {
            communityName,
            moveDate,
        };
    }

    private invoicesTab() {
        return (
            <Row type="flex" className="collapsible">
                <Col className="full-width">
                    <InvoiceTab
                        clientId={this.props.placement.id}
                        invoiceInput={this.getInvoiceInputs()}
                    />
                </Col>
            </Row>
        );
    }

    private filesTab() {
        return (
            <FilesControl
                permissions="editable"
                entityId={this.props.placement.id}
                entityType={FileEntityType.Client}
                transferSubject={{ kind: "placement", id: this.props.placement.id }}
            />
        );
    }

    private communityWorkflowOptionsTab(): JSX.Element {
        return (
            <CommunityComparisonOrderable
                clientId={this.props.placement.id}
                onClientCommunityOrderChange={this.props.onClientCommunityOrderChange}
                placement={this.props.placement}
                locale={this.props.locale}
            />
        );
    }
    private communityWorkflowFindIdealTab(): JSX.Element {
        return (
            <ClientCommunity
                placement={this.props.placement}
                questions={this.props.questions}
            />
        );
    }

    private copyBestLocationAddress() {
        this.props.onFieldChangeMulti({
            bestLocationAddress: this.props.placement.bestContactEntity.address1,
            bestLocationCity: this.props.placement.bestContactEntity.city,
            bestLocationState: this.props.placement.bestContactEntity.state,
            bestLocationZip: this.props.placement.bestContactEntity.zip,
            useBestContactAddressForBestLocation: true,
        });
    }

    private idealLocationReadOnly(): JSX.Element {
        return twoLineFormattedAddress(
            this.props.placement.bestLocationAddress,
            this.props.placement.bestLocationCity,
            this.props.placement.bestLocationState,
            this.props.placement.bestLocationZip,
        );
    }

    private idealLocationInputFields(): JSX.Element {
        const editableIdealLocationField = (label: string, fieldName: string) => {
            return (
                <AutosavingInput
                    label={label}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
                    initial={(this.props.placement as any)[fieldName]}
                    onCommit={newValue => this.props.onFieldChange(fieldName, newValue)}
                />
            );
        };

        return (
            <LocaleContext.Consumer>
                {locale => (
                    <>
                        {editableIdealLocationField("Address", "bestLocationAddress")}
                        <div className="flex space-x-2 items-center">
                            {editableIdealLocationField("City", "bestLocationCity")}
                            <AutosavingStateInput
                                label={locale.strings.state}
                                initial={this.props.placement.bestLocationState}
                                onCommit={newValue =>
                                    this.props.onFieldChange(
                                        "bestLocationState",
                                        newValue,
                                    )
                                }
                            />
                            {editableIdealLocationField(
                                locale.strings.zip,
                                "bestLocationZip",
                            )}
                        </div>
                    </>
                )}
            </LocaleContext.Consumer>
        );
    }

    private areBestLocationFieldsAllEmpty() {
        return (
            !this.props.placement.bestLocationAddress &&
            !this.props.placement.bestLocationCity &&
            !this.props.placement.bestLocationState &&
            !this.props.placement.bestLocationZip
        );
    }

    private async onChangeUseBestContactAddressForBestLocation(newValue: boolean) {
        if (!newValue) {
            // We're unchecking the box. No extra data to update.
            this.props.onFieldChange("useBestContactAddressForBestLocation", newValue);
        } else if (!this.areBestLocationFieldsAllEmpty()) {
            const shouldOverwrite = await fancyConfirm(
                "Copy Best Contact's Address?",
                "This will copy the Best Contact's address to the Ideal Location and overwrite the address you have currently specified.",
                "Yes, copy",
                "Cancel",
            );
            if (shouldOverwrite) {
                this.copyBestLocationAddress();
            }
        } else {
            // The box is being checked, but nothing will get overwritten.
            // Just copy it now!
            this.copyBestLocationAddress();
        }
    }

    private idealLocationSection(): JSX.Element {
        return (
            <InputFormBlock>
                <div className="flex items-center justify-between">
                    <div className="flex-column">
                        <Header iconName="16_IdealLocation">Ideal Location</Header>
                    </div>
                    <div className="flex-column">
                        <CheckboxUncontrolled
                            initial={
                                this.props.placement.useBestContactAddressForBestLocation
                            }
                            onChange={e =>
                                this.onChangeUseBestContactAddressForBestLocation(
                                    e.target.checked,
                                )
                            }
                            label="Use Best Contact's Address"
                        />
                    </div>
                </div>
                <div className="space-y-2 md:space-y-3">
                    {this.props.placement.useBestContactAddressForBestLocation
                        ? this.idealLocationReadOnly()
                        : this.idealLocationInputFields()}

                    <RegionSelect
                        selectedRegionKey={this.props.placement.region}
                        onChange={newVal => {
                            this.props.onFieldChange("region", newVal);
                            this.props.navigateToClients();
                        }}
                    />
                    <AutosavingInput
                        label="Location Notes / Other Preferences"
                        multiLine={true}
                        initial={this.props.placement.otherLocationNotes}
                        onCommit={newVal =>
                            this.props.onFieldChange("otherLocationNotes", newVal)
                        }
                    />
                </div>
            </InputFormBlock>
        );
    }

    private leftColumnQuestions(): JSX.Element {
        return (
            <>
                {this.idealLocationSection()}
                <ClientQuestionSection
                    category={Common.QuestionCategories.finance}
                    clientId={this.props.placement.id}
                />
                <InsuranceDetails
                    clientId={this.props.placement.id}
                    onFieldChange={this.props.onFieldChange}
                />
                <ClientQuestionSection
                    category={Common.QuestionCategories.activity}
                    clientId={this.props.placement.id}
                />
                <ClientQuestionSection
                    category={Common.QuestionCategories.otherDetails}
                    clientId={this.props.placement.id}
                />
                <ClientQuestionSection
                    category={Common.QuestionCategories.socialInterests}
                    clientId={this.props.placement.id}
                />
            </>
        );
    }

    private rightColumnQuestions(): JSX.Element {
        return (
            <>
                <ClientQuestionSection
                    category={Common.QuestionCategories.type}
                    clientId={this.props.placement.id}
                />
                <ClientQuestionSection
                    category={Common.QuestionCategories.staff}
                    clientId={this.props.placement.id}
                />
                <CareProviders clientId={this.props.placement.id} />
                <ClientQuestionSection
                    category={Common.QuestionCategories.services}
                    clientId={this.props.placement.id}
                />
                <ClientQuestionSection
                    category={Common.QuestionCategories.amenity}
                    clientId={this.props.placement.id}
                />
            </>
        );
    }

    private needsTab() {
        return (
            <NgGrid>
                <NgGrid.Column>{this.leftColumnQuestions()}</NgGrid.Column>
                <NgGrid.Column>{this.rightColumnQuestions()}</NgGrid.Column>
            </NgGrid>
        );
    }

    private overviewTab() {
        return (
            <PlacementOverviewTab
                placement={this.props.placement}
                onFieldChange={this.props.onFieldChange}
                onFieldChangeMulti={this.props.onFieldChangeMulti}
                onSendDisclosure={this.overlayClientDisclosureEmail}
            />
        );
    }

    private dialogs(): JSX.Element {
        const { overlayExperience } = this.state;
        if (overlayExperience === OverlayExperience.None) {
            return null;
        }
        if (overlayExperience === OverlayExperience.ShareLink) {
            return (
                <LiveCommunityComparisonLink
                    onDismiss={this.closeOverlay}
                    clientId={this.props.placement.id}
                />
            );
        }
        if (overlayExperience === OverlayExperience.ClientIntakeFaxToCommunities) {
            return (
                <ClientIntakeFax
                    clientId={this.props.placement.id}
                    onDismiss={this.closeOverlay}
                />
            );
        }
        if (overlayExperience === OverlayExperience.ClientIntakeDownload) {
            return (
                <ClientIntakeDownloadDialog
                    clientId={this.props.placement.id}
                    onDismiss={this.closeOverlay}
                />
            );
        }
        if (overlayExperience === OverlayExperience.ClientIntakeUpload) {
            return (
                <ClientIntakeUploadDialog
                    clientId={this.props.placement.id}
                    onDismiss={this.closeOverlay}
                    triggerClientLoad={this.props.triggerClientLoad}
                />
            );
        }
        if (overlayExperience === OverlayExperience.ClientIntakeEmailToCommunities) {
            return (
                <ClientIntakeEmailDialogToCommunity
                    placement={this.props.placement}
                    onDismiss={this.closeOverlay}
                />
            );
        }
        if (overlayExperience === OverlayExperience.CommunityComparisonDownload) {
            return (
                <CommunitiesComparePdfDialog
                    action={`/api/clients/${this.props.placement.id}/communities/pdf`}
                    onDismiss={this.closeOverlay}
                />
            );
        }
        if (overlayExperience === OverlayExperience.CommunityComparisonEmail) {
            return (
                <MessageCompose
                    entityId={this.props.placement.id}
                    entityType={AdvancedSearchEntityType.Client}
                    requestSpecialMessageExperience="community-comparison"
                    renderCommunityComparison={(
                        props: SpecialComposeCommmunityComparisonProps,
                    ) => <EmailCommunityComparisonCustomization {...props} />}
                    onDismiss={this.closeOverlay}
                />
            );
        }
        if (
            overlayExperience === OverlayExperience.GenericClientEmail ||
            overlayExperience === OverlayExperience.ClientDisclosureEmail
        ) {
            return (
                <ClientIntakeEmailDialogToClient
                    placement={this.props.placement}
                    onDismiss={this.closeOverlay}
                    includeDisclosure={
                        overlayExperience === OverlayExperience.ClientDisclosureEmail
                    }
                />
            );
        }
        if (overlayExperience === OverlayExperience.Signature) {
            return (
                <SignatureRequestPanel
                    fileEntityType={FileEntityType.Client}
                    entityId={this.props.placement.id}
                    isOpen={true}
                    onDismiss={this.closeOverlay}
                />
            );
        }
        if (overlayExperience === OverlayExperience.TransferClient) {
            return (
                <TransferClientPanel
                    subject={{ kind: "placement", id: this.props.placement.id }}
                    displayName={this.props.placement.preferredClientName}
                    location={placementTransferLocation(this.props.placement)}
                    onDismiss={this.closeOverlay}
                />
            );
        }
        if (overlayExperience === OverlayExperience.ManageCollaborators) {
            return (
                <ManageCollaboratorsModal
                    clientId={this.props.placement.id}
                    clientName={this.props.placement.preferredClientName}
                    onDismiss={this.closeOverlay}
                />
            );
        }
        if (overlayExperience === OverlayExperience.ShareInputFormLink) {
            return (
                <ShareInputFormLinkPanel
                    placement={this.props.placement}
                    onDismiss={this.closeOverlay}
                />
            );
        }
        if (overlayExperience === OverlayExperience.NotifyUnselectedCommunities) {
            return (
                <NotifyUnselectedCommunitiesDialog
                    placement={this.props.placement}
                    onDismiss={this.closeOverlay}
                />
            );
        }

        const exhaustiveCheck: never = overlayExperience;
        throw new Error(
            `Unhandled overlay experience type: ${OverlayExperience[exhaustiveCheck]}`,
        );
    }

    render() {
        const communityOptionsCount =
            this.props.placement?.clientCommunities?.filter(
                cc => cc.relationship !== Common.CommunityRelationshipType.excluded,
            ).length || undefined;
        const subpages: SubnavItem[] = [
            {
                linkText: "Overview",
                subpage: "overview",
                render: this.overviewTab,
            },
            {
                linkText: "Intake Details",
                subpage: "needs",
                render: this.needsTab,
            },
            {
                linkText: "Find Ideal Communities",
                subpage: "communities",
                render: this.communityWorkflowFindIdealTab,
            },
            {
                linkText: "Community Options",
                subpage: "compareCommunities",
                render: this.communityWorkflowOptionsTab,
                badge: communityOptionsCount
                    ? { count: communityOptionsCount, type: "info" as const }
                    : undefined,
            },
            this.props.showReferralsTab
                ? {
                      linkText: `Referrals`,
                      subpage: "referrals",
                      render: this.referralsTab,
                  }
                : null,
            this.props.showInvoicesTab
                ? {
                      linkText: `Invoices`,
                      subpage: "invoices",
                      render: this.invoicesTab,
                  }
                : null,
            {
                linkText: <MessagesTabHeader />,
                subpage: "emailsSent",
                render: this.emailsSentTab,
            },
            this.props.placementDetailVNext
                ? null
                : ({
                      linkText: "Tasks",
                      subpage: "tasks",
                      render: this.tasksTab,
                      badge: <TaskBadge parentId={this.props.placement.id} />,
                  } as SubnavItem),
            {
                linkText: "Files",
                subpage: "files",
                badge: { count: this.props.fileCount, type: "info" as const },
                render: this.filesTab,
            },
            {
                linkText: "History",
                subpage: "history",
                render: this.activityTab,
            },
        ].filter(x => !!x);

        return (
            <div>
                {this.state.taskAlertsEnabled ? (
                    <NoActiveTasksPrompt placement={this.props.placement} />
                ) : null}
                <div className="row show-client-control">
                    <div className="col component-wrapper">
                        {this.headerBar()}
                        <EngagementTabs
                            current={{
                                kind: "placement",
                                id: this.props.placement.id,
                            }}
                        />
                        {this.archivedMoveBanner()}
                        {this.duplicateWarning()}
                        {this.transferClientBanner()}
                        {this.dialogs()}
                        {this.props.placementDetailVNext ? (
                            <ClientDetailsBlock
                                placement={this.props.placement}
                                editing={this.state.detailsEditing}
                                onFieldChange={this.props.onFieldChange}
                                onFieldChangeMulti={this.props.onFieldChangeMulti}
                            />
                        ) : null}
                        <ShowClientActionBar
                            reactivate={this.props.reactivate}
                            placement={this.props.placement}
                            goToTab={(section: Subpage) =>
                                this.props.selectSection(section)
                            }
                            hasActiveSubpage={!!this.props.activeSubpage}
                            onFieldChange={this.props.onFieldChange}
                        />
                        <ImportantNoteBanner
                            entityId={this.props.placement.id}
                            className="px-4 pb-3"
                        />
                        {this.props.placementDetailVNext && this.props.aiSummary ? (
                            <AiSummaryBanner
                                aiSummary={this.props.aiSummary}
                                className="px-4 pb-3"
                            />
                        ) : null}
                        <DetailPageLayout>
                            <Shared.Nav.Subnav
                                navigate={this.props.selectSection}
                                selectedSubpage={this.props.activeSubpage}
                                subpages={subpages}
                                closeSection={this.props.closeSection}
                                defaultSubpage="overview"
                            />
                        </DetailPageLayout>
                    </div>
                </div>

                <QueryInvalidator ref={this.invalidatorRef} />
            </div>
        );
    }
}

function mapStateToProps(
    state: ApplicationState,
    ownProps: ShowClientOwnProps & ShowClientPropsFromFeatures,
): ShowClientProps {
    return {
        ...ownProps,
        showInvoicesTab:
            isAllowed(ActionType.ViewOwnInvoices, state.permissions, state.region) ||
            isAllowed(ActionType.ViewAllInvoices, state.permissions, state.region),
        showReferralsTab: isAllowed(
            ActionType.LoadReferralsForEntity,
            state.permissions,
            state.region,
        ),
        locale: currentLocale(state),
        responsiveMode: state.responsive.mode,
        shareInputFormLinkEnabled: isAllowed(
            ActionType.ReadHostedForm,
            state.permissions,
            state.region,
        ),
    };
}

const ConnectedShowClient = connect(mapStateToProps)(ShowClient);

const ShowClientVNext: React.FC<
    ShowClientOwnProps & { resolveDeleteNavigation: () => string }
> = props => {
    const aiSummary = useClientAiSummary(props.placement.id);

    return (
        <ConnectedShowClient
            {...props}
            placementDetailVNext={true}
            aiSummary={aiSummary}
        />
    );
};

const ShowClientWithFeatures: React.FC<ShowClientOwnProps> = props => {
    const placementDetailVNext = useFeature("placementDetailVNext");
    const resolveDeleteNavigation = useDeleteNavigation({
        kind: "placement",
        id: props.placement.id,
    });

    if (placementDetailVNext) {
        return (
            <ShowClientVNext
                {...props}
                resolveDeleteNavigation={resolveDeleteNavigation}
            />
        );
    }
    return (
        <ConnectedShowClient
            {...props}
            placementDetailVNext={false}
            resolveDeleteNavigation={resolveDeleteNavigation}
            aiSummary={null}
        />
    );
};

export { ShowClientWithFeatures as ShowClient };
